import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { serviceZoneService } from '../../../services/serviceZoneService';

const ServiceAreaMap = () => {
  const [postalCode, setPostalCode] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceAreas, setServiceAreas] = useState([]);

  useEffect(() => {
    loadServiceAreas();
  }, []);

  const loadServiceAreas = async () => {
    try {
      const zones = await serviceZoneService?.getActiveZones();
      const uniqueAreas = [];
      const seenNames = new Set();

      zones?.forEach(zone => {
        if (zone?.municipalityName && !seenNames?.has(zone?.municipalityName)) {
          seenNames?.add(zone?.municipalityName);
          const postalPrefix = zone?.postalCode?.substring(0, 2);
          uniqueAreas?.push(`${zone?.municipalityName} (${postalPrefix})`);
        }
      });

      setServiceAreas(uniqueAreas?.slice(0, 8));
    } catch (error) {
      console.error('Error loading service areas:', error);
      setServiceAreas([
        'Paris (75)',
        'Hauts-de-Seine (92)',
        'Seine-Saint-Denis (93)',
        'Val-de-Marne (94)'
      ]);
    }
  };

  const handleVerifyPostalCode = async () => {
    if (!postalCode) return;
    
    setLoading(true);
    try {
      const isValid = await serviceZoneService?.validateLocation(postalCode, null, null, null);
      
      if (isValid) {
        setVerificationResult({
          available: true,
          message: 'Excellent ! Nous intervenons dans votre secteur.'
        });
      } else {
        setVerificationResult({
          available: false,
          message: 'Désolé, ce secteur n\'est pas encore couvert. Contactez-nous pour plus d\'informations.'
        });
      }
    } catch (error) {
      console.error('Postal code verification error:', error);
      setVerificationResult({
        available: false,
        message: 'Erreur lors de la vérification. Veuillez réessayer.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="marketing-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="marketing-kicker mb-4">Près de chez vous</p>
            <h2 className="marketing-title text-3xl lg:text-5xl font-heading font-bold mb-4">
              Zone <span className="marketing-accent">d'intervention</span>
            </h2>
            <p className="marketing-muted text-lg mb-6">
              Nous intervenons dans toute l'Île-de-France avec des temps de réponse optimisés.
            </p>
            
            <div className="glass-panel rounded-2xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Vérifiez votre code postal</h3>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="Ex: 75001"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e?.target?.value)}
                  maxLength={5}
                  className="flex-1"
                />
                <Button
                  variant="default"
                  iconName="Search"
                  onClick={handleVerifyPostalCode}
                  loading={loading}
                >
                  Vérifier
                </Button>
              </div>
              
              {verificationResult && (
                <div className={`mt-4 p-4 rounded-lg ${verificationResult?.available ? 'bg-green-500/10' : 'bg-yellow-500/10'}`}>
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={verificationResult?.available ? 'CheckCircle2' : 'AlertCircle'} 
                      size={20} 
                      style={{ color: verificationResult?.available ? '#22c55e' : '#eab308' }}
                    />
                    <span className="font-medium" style={{ color: verificationResult?.available ? '#22c55e' : '#eab308' }}>
                      {verificationResult?.message}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {serviceAreas?.map((area, index) => (
                <div 
                  key={index}
                  className="marketing-muted flex items-center gap-2 text-sm"
                >
                  <Icon name="MapPin" size={16} className="marketing-accent" />
                  <span>{area}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="glass-panel rounded-2xl p-8">
              <div className="aspect-square rounded-xl flex items-center justify-center bg-cyan-300/5 border border-cyan-300/10">
                <div className="text-center">
                  <Icon name="Map" size={80} style={{ color: "rgba(59, 130, 246, 0.3)" }} className="mb-4 mx-auto" />
                  <p className="marketing-muted">Carte de la zone d'intervention</p>
                  <p className="marketing-muted text-sm mt-2">Paris et Île-de-France</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;